import {
  BaseLinkData,
  BaseLinkLabelData,
  MappingLabel,
  ModelConceptHierarchyLabel,
  ModelConceptRelationshipLabel,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptHierarchy,
  isModelConceptRelationship,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import { getMappingKey } from 'src/containers/ProjectDiagram/utils/mapping/createMapping';
import {
  getLabelKeyFromMappingKey,
  getMappingLabelKey,
} from 'src/containers/ProjectDiagram/utils/mapping/createMappingLabel';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getModelConceptHierarchyKey } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchy';
import {
  getLabelKeyFromHierarchyKey,
  getModelConceptHierarchyLabelKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchyLabel';
import { getModelConceptRelationshipKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import {
  getLabelKeyFromRelationshipKey,
  getModelConceptRelationshipLabelKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';

export const updateLinkIris =
  (prevIri: string, newIri: string) =>
  (model: Model): Record<string, string> => {
    const updatedKeys: Record<string, string> = {};

    model.linkDataArray.forEach((link) => {
      const fromIri = link.from
        ? getModelConceptClassValue(link.from)
        : undefined;
      const toIri = link.to ? getModelConceptClassValue(link.to) : undefined;

      let { value } = link;
      let newLinkKey: string | undefined;
      let newLinkLabelKey: string | undefined;
      let linkLabel: BaseLinkLabelData | undefined;

      // set the new relationship key
      if (isModelConceptRelationship(link)) {
        linkLabel = model.nodeDataArray.find(
          (node): node is ModelConceptRelationshipLabel =>
            node.key === getLabelKeyFromRelationshipKey(link.key)
        );

        if (link.value === prevIri) {
          value = newIri;
        }
        newLinkKey = getModelConceptRelationshipKey({
          fromIri,
          value,
          toIri,
        });
        newLinkLabelKey = getModelConceptRelationshipLabelKey({
          fromIri,
          value,
          toIri,
        });
      }
      // set the new hierarchy keys
      else if (isModelConceptHierarchy(link)) {
        linkLabel = model.nodeDataArray.find(
          (node): node is ModelConceptHierarchyLabel =>
            node.key === getLabelKeyFromHierarchyKey(link.key)
        );

        newLinkKey = getModelConceptHierarchyKey({
          fromIri: fromIri as string,
          toIri: toIri as string,
        });
        newLinkLabelKey = getModelConceptHierarchyLabelKey({
          fromIri: fromIri as string,
          value,
          toIri: toIri as string,
        });
      }
      // set the new mapping key
      else if (isMapping(link) && link.to) {
        linkLabel = model.nodeDataArray.find(
          (node): node is MappingLabel =>
            node.key === getLabelKeyFromMappingKey(link.key)
        );

        const fromId = getResourceValue(link.from);
        newLinkKey = getMappingKey({
          fromId,
          value,
          toKey: link.to,
        });
        newLinkLabelKey = getMappingLabelKey({
          fromId,
          value,
          toKey: link.to,
        });
      } else if (
        isRelationshipMapping(link) &&
        link.relationshipIri === prevIri
      ) {
        setDataProperty<RelationshipMapping>(model)(
          link,
          'relationshipIri',
          newIri
        );
      } else if (isRelationshipMapping(link) && link.fromClassIri === prevIri) {
        setDataProperty<RelationshipMapping>(model)(
          link,
          'fromClassIri',
          newIri
        );
      } else if (isRelationshipMapping(link) && link.toClassIri === prevIri) {
        setDataProperty<RelationshipMapping>(model)(link, 'toClassIri', newIri);
      } else {
        return;
      }

      // upate link label data
      if (linkLabel && newLinkLabelKey) {
        updatedKeys[linkLabel.key] = newLinkLabelKey;
        model.setKeyForNodeData(linkLabel, newLinkLabelKey);
        setDataProperty<BaseLinkLabelData>(model)(linkLabel, 'value', value);
        setDataProperty<BaseLinkLabelData>(model)(linkLabel, 'from', link.from);
        setDataProperty<BaseLinkLabelData>(model)(linkLabel, 'to', link.to);
      }

      // update link data
      if (newLinkKey && newLinkLabelKey && !isRelationshipMapping(link)) {
        updatedKeys[link.key] = newLinkKey;
        setDataProperty<BaseLinkData>(model)(link, 'value', value);
        setDataProperty<BaseLinkData>(model)(link, 'labelKeys', [
          newLinkLabelKey,
        ]);
        model.setKeyForLinkData(link, newLinkKey);
      }
    });

    return updatedKeys;
  };
